/**
 * `metadata.versions` of an evaluation (backend: src/evaluation_versions.py): the backend build, every model the
 * evaluation used - where it ran (GPU pod, backend, or reused from an earlier evaluation) - and the pods.
 *
 * Open-ended on purpose: the pipeline adds models and the pods add fields over time. Everything is read
 * defensively, models this admin does not know keep their key as the label, and unknown fields are kept as
 * `extra` so the detail still shows them.
 */

export interface VersionField {
  key: string;
  value: string;
}

export interface ModelVersion {
  key: string;
  /** "pod" | "backend" | "reused" (or a newer value); null when the model did not run */
  runsOn: string | null;
  name: string | null;
  version: string | null;
  file: string | null;
  sha256: string | null;
  /** endpoint of the GPU pod that answered (see `EvaluationVersions.pods`) */
  pod: string | null;
  /** why the model did not run */
  unavailable: string | null;
  /** runsOn "reused": the entry of the evaluation that computed the result (null = not recorded there) */
  reusedFrom: ModelVersion | null;
  extra: VersionField[];
}

export interface PodBuild {
  endpoint: string;
  image: string | null;
  git: string | null;
  extra: VersionField[];
}

export interface EvaluationVersions {
  backend: { version: string | null; gitSha: string | null };
  models: ModelVersion[];
  pods: PodBuild[];
}

export interface SettingsAudit {
  version: string | null;
  overrides: number;
}

const MODEL_FIELDS = new Set(["key", "runs_on", "name", "version", "file", "sha256", "pod", "unavailable", "reused_from"]);
const POD_FIELDS = new Set(["endpoint", "image", "git"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") return null;
  return typeof value === "string" ? value : formatFieldValue(value);
};

/** a stored value as one line: lists joined, objects as JSON */
export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(formatFieldValue).filter(Boolean).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function extraFields(raw: Record<string, unknown>, known: Set<string>): VersionField[] {
  return Object.entries(raw)
    .filter(([key, value]) => !known.has(key) && value !== null && value !== undefined)
    .map(([key, value]) => ({ key, value: formatFieldValue(value) }))
    .filter((field) => field.value !== "");
}

function readModel(raw: Record<string, unknown>, key: string): ModelVersion {
  return {
    key,
    runsOn: text(raw.runs_on),
    name: text(raw.name),
    version: text(raw.version),
    file: text(raw.file),
    sha256: text(raw.sha256),
    pod: text(raw.pod),
    unavailable: text(raw.unavailable),
    reusedFrom: isRecord(raw.reused_from) ? readModel(raw.reused_from, key) : null,
    extra: extraFields(raw, MODEL_FIELDS),
  };
}

export function readEvaluationVersions(raw: unknown): EvaluationVersions | null {
  if (!isRecord(raw)) return null;
  const backend = isRecord(raw.backend) ? raw.backend : {};
  const models = Array.isArray(raw.models)
    ? raw.models.filter(isRecord).flatMap((model) => {
        const key = text(model.key);
        return key ? [readModel(model, key)] : [];
      })
    : [];
  const pods = Array.isArray(raw.pods)
    ? raw.pods.filter(isRecord).flatMap((pod) => {
        const endpoint = text(pod.endpoint);
        return endpoint
          ? [{ endpoint, image: text(pod.image), git: text(pod.git), extra: extraFields(pod, POD_FIELDS) }]
          : [];
      })
    : [];
  return { backend: { version: text(backend.version), gitSha: text(backend.git_sha) }, models, pods };
}

/** `metadata.evaluation_settings`: {version, overrides} of the runtime settings the verdict was made with */
export function readSettingsAudit(raw: unknown): SettingsAudit | null {
  if (!isRecord(raw)) return null;
  return {
    version: text(raw.version),
    overrides: isRecord(raw.overrides) ? Object.keys(raw.overrides).length : 0,
  };
}

/** "cutnet v8", or the file when the model has no name / version */
export function modelTitle(model: ModelVersion): string | null {
  const title = [model.name, model.version].filter(Boolean).join(" ");
  return title || model.file;
}
